import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2, ShieldCheck, Plus } from 'lucide-react';
import { Dialog } from '@/shared/ui/Dialog';
import { Button } from '@/shared/ui/Button';
import { Input } from '@/shared/ui/Input';
import { Spinner } from '@/shared/ui/Spinner';
import { useToast } from '@/shared/ui/toast';
import { ApiException } from '@/shared/api/client';
import { contactSchema, type ContactFormValues } from '@/shared/schemas/customer.schema';
import { useCustomer, useAddContact, useDeleteContact } from './customers.api';

export function CustomerDetailDialog({
  customerId,
  onClose,
}: {
  customerId: string | null;
  onClose: () => void;
}) {
  const { data, isLoading } = useCustomer(customerId);
  const addContact = useAddContact();
  const delContact = useDeleteContact();
  const push = useToast((s) => s.push);
  const [showContactForm, setShowContactForm] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: { approvalAuthorized: false },
  });

  const onAddContact = async (values: ContactFormValues) => {
    if (!customerId) return;
    try {
      const input = {
        name: values.name,
        ...(values.email ? { email: values.email } : {}),
        ...(values.phone ? { phone: values.phone } : {}),
        approvalAuthorized: values.approvalAuthorized ?? false,
      };
      await addContact.mutateAsync({ customerId, input });
      push('Ansprechpartner hinzugefügt.');
      reset({ approvalAuthorized: false });
      setShowContactForm(false);
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Fehler.', 'error');
    }
  };

  const onDelContact = async (contactId: string) => {
    if (!customerId) return;
    try {
      await delContact.mutateAsync({ customerId, contactId });
      push('Ansprechpartner entfernt.');
    } catch (e) {
      push(e instanceof ApiException ? e.message : 'Fehler.', 'error');
    }
  };

  return (
    <Dialog open={!!customerId} onClose={onClose} title={data?.companyName ?? 'Kunde'}>
      {isLoading || !data ? (
        <Spinner />
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-2 gap-3 text-sm">
            {data.billingAddress && (
              <div className="col-span-2">
                <span className="text-slate-500">Rechnungsadresse</span>
                <p className="text-slate-900">{data.billingAddress}</p>
              </div>
            )}
            {data.email && (
              <div>
                <span className="text-slate-500">E-Mail</span>
                <p className="text-slate-900">{data.email}</p>
              </div>
            )}
            {data.phone && (
              <div>
                <span className="text-slate-500">Telefon</span>
                <p className="text-slate-900">{data.phone}</p>
              </div>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Ansprechpartner</h3>
              <button
                onClick={() => setShowContactForm((v) => !v)}
                className="inline-flex items-center gap-1 text-sm text-brand-600 hover:underline"
              >
                <Plus className="h-4 w-4" /> Hinzufügen
              </button>
            </div>

            {data.contacts.length === 0 && !showContactForm && (
              <p className="text-sm text-slate-400">Noch keine Ansprechpartner.</p>
            )}

            <ul className="space-y-2">
              {data.contacts.map((c) => (
                <li
                  key={c.id}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2"
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-900">{c.name}</span>
                      {c.approvalAuthorized && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-xs text-green-700">
                          <ShieldCheck className="h-3 w-3" /> freigabeberechtigt
                        </span>
                      )}
                    </div>
                    {c.email && <p className="text-xs text-slate-500">{c.email}</p>}
                  </div>
                  <button
                    onClick={() => onDelContact(c.id)}
                    className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>

            {showContactForm && (
              <form
                onSubmit={handleSubmit(onAddContact)}
                className="mt-3 space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3"
              >
                <Input label="Name *" {...register('name')} error={errors.name?.message} />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="E-Mail" type="email" {...register('email')} error={errors.email?.message} />
                  <Input label="Telefon" {...register('phone')} error={errors.phone?.message} />
                </div>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" {...register('approvalAuthorized')} className="h-4 w-4 rounded" />
                  Darf Zusatzleistungen freigeben
                </label>
                <div className="flex justify-end">
                  <Button type="submit" size="md" loading={isSubmitting}>
                    Hinzufügen
                  </Button>
                </div>
              </form>
            )}
          </div>

          {data.objects.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold text-slate-700">Objekte ({data.objects.length})</h3>
              <ul className="flex flex-wrap gap-2">
                {data.objects.map((o) => (
                  <li key={o.id} className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-700">
                    {o.name}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Dialog>
  );
}
